import React, { useState } from 'react';
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import WidgetWrapper from './WidgetWrapper';

/**
 * PopupWidget - Displays a clickable element that opens a popup dialog
 * This recreates the old RMMS "pop_up_widget" type
 *
 * Features:
 * - Clickable button/icon that opens a dialog
 * - Tag-based conditional display (show popup based on tag value)
 * - Auto-show on value change (optional)
 * - Customizable popup content
 * - Different icon types (info, warning, error, notification)
 */
const PopupWidget = ({
  text = 'Show Details',
  tagId = null,
  tagValue = null,
  // Popup configuration
  popupTitle = 'Information',
  popupMessage = 'No message configured',
  popupType = 'info', // 'info', 'warning', 'error', 'notification'
  popupContent = null, // Custom JSX content for popup body
  // Auto-show behavior
  autoShow = false, // Auto-show popup when conditions are met
  showOnValue = null, // Show popup when tag value equals this
  showOnChange = false, // Show popup when tag value changes
  // Button appearance
  buttonText = '',
  showIcon = true,
  iconSize = 'medium', // 'small', 'medium', 'large'
  buttonVariant = 'contained', // 'contained', 'outlined', 'text'
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  isEditMode = false,
  ...props
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [lastTagValue, setLastTagValue] = useState(tagValue);

  // Auto-show logic
  React.useEffect(() => {
    if (isEditMode || !autoShow) return;

    // Show on specific value
    if (showOnValue !== null && tagValue === showOnValue) {
      setDialogOpen(true);
    }

    // Show on value change
    if (showOnChange && tagValue !== lastTagValue && lastTagValue !== null) {
      setDialogOpen(true);
    }

    setLastTagValue(tagValue);
  }, [tagValue, showOnValue, showOnChange, autoShow, isEditMode, lastTagValue]);

  const handleOpen = () => {
    if (isEditMode) {
      return;
    }
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
  };

  // Get icon based on popup type
  const getIcon = () => {
    switch (popupType) {
      case 'warning':
        return <WarningIcon fontSize={iconSize} />;
      case 'error':
        return <ErrorIcon fontSize={iconSize} />;
      case 'notification':
        return <NotificationsIcon fontSize={iconSize} />;
      case 'info':
      default:
        return <InfoIcon fontSize={iconSize} />;
    }
  };

  // Get color based on popup type
  const getTypeColor = () => {
    switch (popupType) {
      case 'warning':
        return '#ff9800';
      case 'error':
        return '#f44336';
      case 'notification':
        return '#2196f3';
      case 'info':
      default:
        return '#4caf50';
    }
  };

  const typeColor = getTypeColor();
  const displayText = buttonText || text;

  return (
    <>
      <WidgetWrapper
        backgroundColor={backgroundColor}
        foreColor={foreColor}
        borderWidth={borderWidth}
        borderColor={borderColor}
        borderRadius={borderRadius}
        fontSize={fontSize}
        fontWeight={fontWeight}
        fontFamily={fontFamily}
        opacity={opacity}
        padding="8px"
        {...props}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            cursor: isEditMode ? 'default' : 'pointer'
          }}
          onClick={handleOpen}
        >
          {buttonVariant === 'text' ? (
            // Text style button
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: foreColor || typeColor,
                '&:hover': {
                  opacity: isEditMode ? 1 : 0.8
                }
              }}
            >
              {showIcon && getIcon()}
              <Typography
                sx={{
                  fontSize: 'inherit',
                  fontWeight: 'inherit',
                  fontFamily: 'inherit',
                  color: 'inherit'
                }}
              >
                {displayText}
              </Typography>
            </Box>
          ) : (
            // Button style
            <Button
              variant={buttonVariant}
              startIcon={showIcon ? getIcon() : null}
              disabled={isEditMode}
              sx={{
                backgroundColor: buttonVariant === 'contained' ? typeColor : 'transparent',
                color: buttonVariant === 'contained' ? '#fff' : typeColor,
                borderColor: typeColor,
                fontSize: 'inherit',
                fontWeight: 'inherit',
                fontFamily: 'inherit',
                textTransform: 'none',
                '&:hover': {
                  backgroundColor: buttonVariant === 'contained' ? typeColor : 'transparent',
                  opacity: 0.8,
                  borderColor: typeColor
                }
              }}
              fullWidth
            >
              {displayText}
            </Button>
          )}
        </Box>
      </WidgetWrapper>

      {/* Popup Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: typeColor,
            color: '#fff',
            py: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {getIcon()}
            <Typography variant="h6" component="span">
              {popupTitle}
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{ color: '#fff' }}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ mt: 2 }}>
          {/* Custom content or default message */}
          {popupContent ? (
            popupContent
          ) : (
            <Box>
              <Typography variant="body1" paragraph>
                {popupMessage}
              </Typography>

              {/* Show tag value if available */}
              {tagId && tagValue !== null && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    borderLeft: `4px solid ${typeColor}`
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Tag ID: {tagId}
                  </Typography>
                  <Typography variant="h6" sx={{ mt: 1, color: typeColor }}>
                    Current Value: {tagValue}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleClose}
            variant="contained"
            sx={{
              backgroundColor: typeColor,
              '&:hover': {
                backgroundColor: typeColor,
                opacity: 0.8
              }
            }}
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PopupWidget;
